import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { onMounted, ref, nextTick } from 'vue';
import ServiceRecoveryModal from './ServiceRecoveryModal.vue';
import { UserModalsApi } from '@/api/UserModalsApi';

// После того как модалку закрыли — не дёргаем эндпоинт повторно
// в рамках этой сессии вкладки. Ключи свои, чужие не переиспользуем.
const SESSION_SKIP_KEY = 'serviceRecovery.skip';

// Ответ shouldShow=false кэшируем ненадолго, чтобы не делать запрос на каждой загрузке страницы.
const SESSION_NOTHING_KEY = 'serviceRecovery.checkedAt';
const NOTHING_TTL_MS = 10 * 60 * 1000;

// sessionStorage может бросить (запрещённое хранилище, квота) — кэш здесь лишь оптимизация,
// поэтому ошибки хранилища не должны прерывать проверку и dismiss.

export default /*#__PURE__*/_defineComponent({
  __name: 'ServiceRecoveryModals',
  emits: ["checked"],
  setup(__props, { emit: __emit }) {

// Самодостаточный хост модалки «Восстанавливаем работу Finch» (FINCH-2631): сам запрашивает
// getServiceRecoveryModalInfo при входе и показывает модалку.
// Цепочка модалок входа: сначала модалка о сбое (FINCH-2630), затем эта, затем остальные.
// TariffNotificationModals.vue монтирует хост только после checked(false) от модалки о сбое,
// а хост сообщает свой результат событием checked: пока проверка не завершилась или модалка открыта,
// тарифные модалки, апселл и анонс пакетов не запрашиваются. Во вложенном фрейме хост сразу шлёт checked(false).
// checked шлётся при любом исходе, включая ошибки, — иначе хост заблокировал бы все модалки входа.
function readSession(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch (e) {
        console.error('[ServiceRecoveryModals] sessionStorage read failed', e);
        return null;
    }
}

function writeSession(key: string, value: string) {
    try {
        sessionStorage.setItem(key, value);
    } catch (e) {
        console.error('[ServiceRecoveryModals] sessionStorage write failed', e);
    }
}

// Во вложенном фрейме (доска внутри звонка) не показываем: модалка всплыла бы поверх доски
// посреди занятия, а хост на странице звонка и хост в iframe доски стартуют параллельно
// (общий sessionStorage ещё пуст) и показали бы две модалки сразу. Покажет хост верхней страницы.
function isNestedFrame(): boolean {
    return window.self !== window.top;
}

const emit = __emit;

const modalRef = ref<InstanceType<typeof ServiceRecoveryModal> | null>(null);
const shouldShow = ref(false);
let dismissed = false;

async function onDismiss() {
    if (!shouldShow.value || dismissed) {
        return;
    }
    dismissed = true;
    writeSession(SESSION_SKIP_KEY, '1');
    // post() не бросает: при отказе возвращает isSuccess=false. Показ тогда не запишется
    // на сервере, и в новой вкладке модалка придёт снова — логируем, чтобы это было видно.
    const result = await new UserModalsApi().dismissServiceRecoveryModal();
    if (!result?.isSuccess) {
        console.error('[ServiceRecoveryModals] dismiss failed', result);
    }
}

async function resolveShouldShow(): Promise<boolean> {
    if (isNestedFrame() || readSession(SESSION_SKIP_KEY)) {
        return false;
    }
    const checkedAt = Number(readSession(SESSION_NOTHING_KEY));
    if (checkedAt && Date.now() - checkedAt < NOTHING_TTL_MS) {
        return false;
    }
    // get() не бросает и без редиректа на /Error: при сбое возвращает isSuccess=false.
    const info = await new UserModalsApi().getServiceRecoveryModalInfo();
    if (info?.entity?.shouldShow) {
        return true;
    }
    // Кэшируем «нечего показывать» только для настоящего ответа бэка,
    // чтобы сбой запроса не прятал модалку на 10 минут.
    if (info?.isSuccess) {
        writeSession(SESSION_NOTHING_KEY, String(Date.now()));
    }
    return false;
}

onMounted(async () => {
    let shown = false;
    try {
        if (await resolveShouldShow()) {
            shouldShow.value = true;
            await nextTick();
            if (modalRef.value) {
                modalRef.value.show();
                shown = true;
            } else {
                console.error('[ServiceRecoveryModals] modal ref is empty, modal not shown');
            }
        }
    } catch (e) {
        console.error('[ServiceRecoveryModals] check failed', e);
    } finally {
        emit('checked', shown);
    }
});

return (_ctx: any,_cache: any) => {
  return (shouldShow.value)
    ? (_openBlock(), _createBlock(ServiceRecoveryModal, {
        key: 0,
        ref_key: "modalRef",
        ref: modalRef,
        onHidden: onDismiss
      }, null, 512))
    : _createCommentVNode("", true)
}
}

})